import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, MapPin, Phone, Mail, ArrowLeft, RefreshCw } from 'lucide-react';

const GeofenceRestricted = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the restriction message from navigation state
  const restrictionMessage = location.state?.message || 
    'You are attempting to access the system from an unauthorized location.';

  const handleTryAgain = () => {
    navigate('/login');
  };

  const handleRefreshLocation = () => {
    // Clear any cached location and redirect to login
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          navigate('/login');
        },
        () => {
          navigate('/login');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-800 to-red-900 p-6">
      <div className="w-full max-w-lg">
        {/* Company Logo */}
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" 
            alt="MBConstruction" 
            className="w-28 h-28 mx-auto mb-2 rounded-2xl object-contain bg-white p-3 shadow-lg"
          />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-red-50 border-b border-red-100 px-8 py-6 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-red-700 mb-2">Access Restricted</h1>
            <p className="text-red-600 text-sm">Location-Based Security Alert</p>
          </div>

          {/* Content Section */}
          <div className="px-8 py-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-slate-700 text-center">{restrictionMessage}</p>
            </div>

            {/* Information Box */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Why am I seeing this?</h3>
                  <p className="text-sm text-slate-600 mb-2">
                    For security purposes, MBConstruction restricts system access 
                    to authorized locations only. You are currently outside your designated work area.
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>To access the system:</strong>
                  </p>
                  <ul className="text-sm text-slate-600 mt-1 list-disc list-inside space-y-1">
                    <li>Move to your authorized work location</li>
                    <li>Ensure your device's location services are enabled</li>
                    <li>Contact your supervisor if you believe this is an error</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Need Help?
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-blue-700">
                  Contact your site supervisor or the admin team for assistance:
                </p>
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <Mail className="w-4 h-4" />
                  <span>admin@mbconstruction.co.za</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleRefreshLocation}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Refresh Location & Try Again
              </button>
              <button
                onClick={handleTryAgain}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Login
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-50 border-t border-slate-200 px-8 py-4 text-center">
            <p className="text-xs text-slate-500">
              MBConstruction (Pty) Ltd | Security Protected
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeofenceRestricted;
